/**
 * AdminFeedbackPage Component
 *
 * Feedback management page for admins.
 * Shows all feedback with filtering, status management, and admin notes.
 */

import { useState, useEffect, type CSSProperties } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
  limit,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import type { Feedback, FeedbackStatus, FeedbackType, FeedbackPriority } from '@flash/shared';

/**
 * Convert Firestore document to Feedback type
 */
const docToFeedback = (docId: string, docData: DocumentData): Feedback => {
  return {
    id: docId,
    description: (docData['description'] as string) ?? '',
    type: (docData['type'] as FeedbackType | null) ?? null,
    priority: (docData['priority'] as FeedbackPriority | null) ?? null,
    screenshotUrl: (docData['screenshotUrl'] as string | null) ?? null,
    canFollowUp: (docData['canFollowUp'] as boolean) ?? false,
    userId: (docData['userId'] as string) ?? '',
    userEmail: (docData['userEmail'] as string) ?? '',
    userDisplayName: (docData['userDisplayName'] as string) ?? '',
    pageUrl: (docData['pageUrl'] as string) ?? '',
    userAgent: (docData['userAgent'] as string) ?? '',
    status: (docData['status'] as FeedbackStatus) ?? 'new',
    createdAt: docData['createdAt'],
    updatedAt: docData['updatedAt'],
    adminNotes: (docData['adminNotes'] as string | null) ?? null,
    resolvedAt: docData['resolvedAt'] ?? null,
    resolvedBy: (docData['resolvedBy'] as string | null) ?? null,
  };
};

/**
 * Validate and sanitize a URL for safe display
 * Only allows http/https URLs to prevent XSS via javascript: or data: URLs
 */
const sanitizeUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Format a Firestore timestamp for display
 */
const formatTimestamp = (timestamp: unknown): string => {
  if (!timestamp) return 'N/A';
  if (typeof timestamp === 'object' && timestamp !== null && 'toDate' in timestamp) {
    return (timestamp as { toDate: () => Date }).toDate().toLocaleString();
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleString();
  }
  return 'N/A';
};

/**
 * Get badge style for feedback type
 */
const getTypeBadgeStyle = (type: FeedbackType | null): CSSProperties => {
  switch (type) {
    case 'bug':
      return { backgroundColor: 'var(--color-error)', color: 'white' };
    case 'feature':
      return {
        backgroundColor: 'var(--color-badge-info-bg)',
        color: 'var(--color-badge-info-text)',
      };
    case 'general':
      return { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' };
    default:
      return { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' };
  }
};

/**
 * Get badge style for priority
 */
const getPriorityBadgeStyle = (priority: FeedbackPriority | null): CSSProperties => {
  switch (priority) {
    case 'high':
      return { backgroundColor: 'var(--color-error)', color: 'white' };
    case 'medium':
      return {
        backgroundColor: 'var(--color-badge-warning-bg)',
        color: 'var(--color-badge-warning-text)',
      };
    case 'low':
      return {
        backgroundColor: 'var(--color-badge-success-bg)',
        color: 'var(--color-badge-success-text)',
      };
    default:
      return { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' };
  }
};

/**
 * Get badge style for status
 */
const getStatusBadgeStyle = (status: FeedbackStatus): CSSProperties => {
  switch (status) {
    case 'new':
      return {
        backgroundColor: 'var(--color-badge-info-bg)',
        color: 'var(--color-badge-info-text)',
      };
    case 'reviewed':
      return {
        backgroundColor: 'var(--color-badge-warning-bg)',
        color: 'var(--color-badge-warning-text)',
      };
    case 'resolved':
      return {
        backgroundColor: 'var(--color-badge-success-bg)',
        color: 'var(--color-badge-success-text)',
      };
    default:
      return { backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' };
  }
};

/**
 * AdminFeedbackPage displays all feedback with management capabilities
 */
export const AdminFeedbackPage = () => {
  const { user: currentUser } = useAuth();

  // Pagination constants
  const PAGE_SIZE = 20;

  // Feedback data
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<FeedbackType | 'all'>('all');

  // Selected feedback for detail view
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  // Admin notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);

  // Fetch initial feedback on mount (with pagination)
  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const db = getFirebaseDb();
        const feedbackQuery = query(
          collection(db, 'feedback'),
          orderBy('createdAt', 'desc'),
          limit(PAGE_SIZE)
        );
        const snapshot = await getDocs(feedbackQuery);
        const feedbackData = snapshot.docs.map((docSnapshot) =>
          docToFeedback(docSnapshot.id, docSnapshot.data())
        );
        setFeedbackList(feedbackData);
        setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
        setHasMore(snapshot.docs.length === PAGE_SIZE);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load feedback');
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  /**
   * Load more feedback (pagination)
   */
  const handleLoadMore = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    setLoadingMore(true);
    try {
      const db = getFirebaseDb();
      const feedbackQuery = query(
        collection(db, 'feedback'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(feedbackQuery);
      const feedbackData = snapshot.docs.map((docSnapshot) =>
        docToFeedback(docSnapshot.id, docSnapshot.data())
      );
      setFeedbackList((prev) => [...prev, ...feedbackData]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] ?? null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more feedback:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Filter feedback
  const filteredFeedback = feedbackList.filter((fb) => {
    if (statusFilter !== 'all' && fb.status !== statusFilter) return false;
    if (typeFilter !== 'all' && fb.type !== typeFilter) return false;
    return true;
  });

  // Stats
  const stats = {
    total: feedbackList.length,
    new: feedbackList.filter((fb) => fb.status === 'new').length,
    reviewed: feedbackList.filter((fb) => fb.status === 'reviewed').length,
    resolved: feedbackList.filter((fb) => fb.status === 'resolved').length,
  };

  /**
   * Update feedback status
   */
  const handleUpdateStatus = async (feedbackId: string, newStatus: FeedbackStatus) => {
    setUpdatingStatus(true);
    setStatusUpdateError(null);
    try {
      const db = getFirebaseDb();
      const feedbackRef = doc(db, 'feedback', feedbackId);

      const updateData: Record<string, unknown> = {
        status: newStatus,
        updatedAt: serverTimestamp(),
      };

      if (newStatus === 'resolved') {
        updateData['resolvedAt'] = serverTimestamp();
        updateData['resolvedBy'] = currentUser?.uid ?? null;
      }

      await updateDoc(feedbackRef, updateData);

      // Update local state
      setFeedbackList((prev) =>
        prev.map((fb) =>
          fb.id === feedbackId
            ? {
                ...fb,
                status: newStatus,
                ...(newStatus === 'resolved'
                  ? { resolvedAt: new Date(), resolvedBy: currentUser?.uid ?? null }
                  : {}),
              }
            : fb
        )
      );

      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback((prev) =>
          prev
            ? {
                ...prev,
                status: newStatus,
                ...(newStatus === 'resolved'
                  ? { resolvedAt: new Date(), resolvedBy: currentUser?.uid ?? null }
                  : {}),
              }
            : null
        );
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setStatusUpdateError(
        err instanceof Error ? err.message : 'Failed to update status. Please try again.'
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  /**
   * Save admin notes
   */
  const handleSaveNotes = async () => {
    if (!selectedFeedback) return;

    setSavingNotes(true);
    try {
      const db = getFirebaseDb();
      const feedbackRef = doc(db, 'feedback', selectedFeedback.id);

      await updateDoc(feedbackRef, {
        adminNotes: notesValue || null,
        updatedAt: serverTimestamp(),
      });

      // Update local state
      setFeedbackList((prev) =>
        prev.map((fb) =>
          fb.id === selectedFeedback.id ? { ...fb, adminNotes: notesValue || null } : fb
        )
      );

      setSelectedFeedback((prev) => (prev ? { ...prev, adminNotes: notesValue || null } : null));
      setEditingNotes(false);
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  /**
   * Open feedback detail
   */
  const handleSelectFeedback = (fb: Feedback) => {
    setSelectedFeedback(fb);
    setNotesValue(fb.adminNotes ?? '');
    setEditingNotes(false);
  };

  /**
   * Close feedback detail
   */
  const handleCloseDetail = () => {
    setSelectedFeedback(null);
    setEditingNotes(false);
  };

  if (loading) {
    return (
      <main style={containerStyle}>
        <div style={loadingContainerStyle}>
          <p style={loadingTextStyle}>Loading feedback...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main style={containerStyle}>
        <div style={contentStyle}>
          <div style={errorContainerStyle}>
            <h2 style={errorTitleStyle}>Error Loading Feedback</h2>
            <p style={errorTextStyle}>{error}</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <header style={headerStyle}>
          <h1 style={titleStyle}>Manage Feedback</h1>
          <p style={subtitleStyle}>View and respond to user feedback</p>
        </header>

        {/* Stats Cards */}
        <div style={statsGridStyle}>
          <div style={statCardStyle}>
            <span style={statValueStyle}>{stats.total}</span>
            <span style={statLabelStyle}>Total</span>
          </div>
          <div style={{ ...statCardStyle, borderColor: 'var(--color-primary)' }}>
            <span style={{ ...statValueStyle, color: 'var(--color-primary)' }}>{stats.new}</span>
            <span style={statLabelStyle}>New</span>
          </div>
          <div style={{ ...statCardStyle, borderColor: 'var(--color-warning)' }}>
            <span style={{ ...statValueStyle, color: 'var(--color-warning)' }}>
              {stats.reviewed}
            </span>
            <span style={statLabelStyle}>Reviewed</span>
          </div>
          <div style={{ ...statCardStyle, borderColor: 'var(--color-success)' }}>
            <span style={{ ...statValueStyle, color: 'var(--color-success)' }}>
              {stats.resolved}
            </span>
            <span style={statLabelStyle}>Resolved</span>
          </div>
        </div>

        {/* Filters */}
        <div style={filtersContainerStyle}>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
              style={filterSelectStyle}
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Type:</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as FeedbackType | 'all')}
              style={filterSelectStyle}
            >
              <option value="all">All</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="general">General</option>
            </select>
          </div>
        </div>

        {/* Main Content - List and Detail Side by Side */}
        <div style={mainGridStyle}>
          {/* Feedback List */}
          <div style={listContainerStyle}>
            {filteredFeedback.length === 0 ? (
              <div style={emptyStateStyle}>
                <p style={emptyTextStyle}>No feedback found</p>
              </div>
            ) : (
              <>
                {filteredFeedback.map((fb) => (
                  <div
                    key={fb.id}
                    style={{
                      ...listItemStyle,
                      ...(selectedFeedback?.id === fb.id ? selectedItemStyle : {}),
                    }}
                    onClick={() => handleSelectFeedback(fb)}
                  >
                    <div style={listItemHeaderStyle}>
                      <span style={{ ...badgeStyle, ...getStatusBadgeStyle(fb.status) }}>
                        {fb.status}
                      </span>
                      {fb.type && (
                        <span style={{ ...badgeStyle, ...getTypeBadgeStyle(fb.type) }}>
                          {fb.type}
                        </span>
                      )}
                      {fb.priority && (
                        <span style={{ ...badgeStyle, ...getPriorityBadgeStyle(fb.priority) }}>
                          {fb.priority}
                        </span>
                      )}
                    </div>
                    <p style={listItemDescriptionStyle}>
                      {fb.description.length > 100
                        ? `${fb.description.substring(0, 100)}...`
                        : fb.description}
                    </p>
                    <div style={listItemFooterStyle}>
                      <span style={listItemMetaStyle}>{fb.userDisplayName || fb.userEmail}</span>
                      <span style={listItemMetaStyle}>{formatTimestamp(fb.createdAt)}</span>
                    </div>
                  </div>
                ))}
                {hasMore && (
                  <div style={loadMoreContainerStyle}>
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      style={loadMoreButtonStyle}
                    >
                      {loadingMore ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Detail Panel */}
          {selectedFeedback && (
            <div style={detailPanelStyle}>
              <div style={detailHeaderStyle}>
                <h2 style={detailTitleStyle}>Feedback Details</h2>
                <button onClick={handleCloseDetail} style={closeDetailButtonStyle}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div style={detailContentStyle}>
                {/* Status & Actions */}
                <div style={detailSectionStyle}>
                  <h3 style={detailSectionTitleStyle}>Status</h3>
                  <div style={statusButtonsStyle}>
                    {(['new', 'reviewed', 'resolved'] as const).map((status) => (
                      <button
                        key={status}
                        onClick={() => handleUpdateStatus(selectedFeedback.id, status)}
                        disabled={updatingStatus || selectedFeedback.status === status}
                        style={{
                          ...statusButtonStyle,
                          ...(selectedFeedback.status === status ? activeStatusButtonStyle : {}),
                        }}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                  {statusUpdateError && <p style={statusErrorStyle}>{statusUpdateError}</p>}
                </div>

                {/* Badges */}
                <div style={detailSectionStyle}>
                  <div style={detailBadgesStyle}>
                    {selectedFeedback.type && (
                      <span style={{ ...badgeStyle, ...getTypeBadgeStyle(selectedFeedback.type) }}>
                        {selectedFeedback.type === 'bug'
                          ? 'Bug Report'
                          : selectedFeedback.type === 'feature'
                            ? 'Feature Request'
                            : 'General'}
                      </span>
                    )}
                    {selectedFeedback.priority && (
                      <span
                        style={{
                          ...badgeStyle,
                          ...getPriorityBadgeStyle(selectedFeedback.priority),
                        }}
                      >
                        {selectedFeedback.priority.charAt(0).toUpperCase() +
                          selectedFeedback.priority.slice(1)}{' '}
                        Priority
                      </span>
                    )}
                    {selectedFeedback.canFollowUp && (
                      <span
                        style={{
                          ...badgeStyle,
                          backgroundColor: 'var(--color-badge-success-bg)',
                          color: 'var(--color-badge-success-text)',
                        }}
                      >
                        Can Follow Up
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div style={detailSectionStyle}>
                  <h3 style={detailSectionTitleStyle}>Description</h3>
                  <p style={detailDescriptionStyle}>{selectedFeedback.description}</p>
                </div>

                {/* Screenshot */}
                {selectedFeedback.screenshotUrl && (
                  <div style={detailSectionStyle}>
                    <h3 style={detailSectionTitleStyle}>Screenshot</h3>
                    <a
                      href={selectedFeedback.screenshotUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={selectedFeedback.screenshotUrl}
                        alt="Feedback screenshot"
                        style={screenshotStyle}
                      />
                    </a>
                  </div>
                )}

                {/* User Info */}
                <div style={detailSectionStyle}>
                  <h3 style={detailSectionTitleStyle}>User</h3>
                  <p style={detailTextStyle}>
                    <strong>Name:</strong> {selectedFeedback.userDisplayName || 'N/A'}
                  </p>
                  <p style={detailTextStyle}>
                    <strong>Email:</strong> {selectedFeedback.userEmail}
                  </p>
                </div>

                {/* Context */}
                <div style={detailSectionStyle}>
                  <h3 style={detailSectionTitleStyle}>Context</h3>
                  <p style={detailTextStyle}>
                    <strong>Page:</strong>{' '}
                    {(() => {
                      const safeUrl = sanitizeUrl(selectedFeedback.pageUrl);
                      return safeUrl ? (
                        <a
                          href={safeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={linkStyle}
                        >
                          {safeUrl}
                        </a>
                      ) : (
                        <span style={invalidUrlStyle}>
                          {selectedFeedback.pageUrl} (invalid URL)
                        </span>
                      );
                    })()}
                  </p>
                  <p style={detailTextStyle}>
                    <strong>Submitted:</strong> {formatTimestamp(selectedFeedback.createdAt)}
                  </p>
                  {selectedFeedback.resolvedAt && (
                    <p style={detailTextStyle}>
                      <strong>Resolved:</strong> {formatTimestamp(selectedFeedback.resolvedAt)}
                    </p>
                  )}
                </div>

                {/* Admin Notes */}
                <div style={detailSectionStyle}>
                  <div style={notesSectionHeaderStyle}>
                    <h3 style={detailSectionTitleStyle}>Admin Notes</h3>
                    {!editingNotes && (
                      <button onClick={() => setEditingNotes(true)} style={editNotesButtonStyle}>
                        {selectedFeedback.adminNotes ? 'Edit' : 'Add Notes'}
                      </button>
                    )}
                  </div>
                  {editingNotes ? (
                    <div>
                      <textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add internal notes about this feedback..."
                        style={notesTextareaStyle}
                        rows={4}
                      />
                      <div style={notesActionsStyle}>
                        <button
                          onClick={() => setEditingNotes(false)}
                          style={cancelNotesButtonStyle}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveNotes}
                          disabled={savingNotes}
                          style={saveNotesButtonStyle}
                        >
                          {savingNotes ? 'Saving...' : 'Save Notes'}
                        </button>
                      </div>
                    </div>
                  ) : selectedFeedback.adminNotes ? (
                    <p style={detailTextStyle}>{selectedFeedback.adminNotes}</p>
                  ) : (
                    <p style={emptyNotesStyle}>No admin notes yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <p style={footerStatsStyle}>
          Showing {filteredFeedback.length} of {feedbackList.length} feedback items
        </p>
      </div>
    </main>
  );
};

/**
 * Styles
 */
const containerStyle: CSSProperties = {
  minHeight: '100%',
  backgroundColor: 'var(--color-bg-secondary)',
};

const contentStyle: CSSProperties = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px',
};

const loadingContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100%',
};

const loadingTextStyle: CSSProperties = {
  fontSize: '1.125rem',
  color: 'var(--color-text-secondary)',
};

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '40px 20px',
  textAlign: 'center',
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
};

const errorTitleStyle: CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  marginBottom: '8px',
};

const errorTextStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '16px',
};

const headerStyle: CSSProperties = {
  marginBottom: '24px',
};

const titleStyle: CSSProperties = {
  fontSize: '1.75rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '12px 0 4px 0',
};

const subtitleStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-secondary)',
  margin: 0,
};

const statsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '16px',
  marginBottom: '24px',
};

const statCardStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '8px',
  padding: '16px',
  textAlign: 'center',
  border: '2px solid var(--color-border)',
};

const statValueStyle: CSSProperties = {
  display: 'block',
  fontSize: '2rem',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
};

const statLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginTop: '4px',
};

const filtersContainerStyle: CSSProperties = {
  display: 'flex',
  gap: '16px',
  marginBottom: '20px',
};

const filterGroupStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const filterLabelStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary)',
};

const filterSelectStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  backgroundColor: 'var(--color-bg-primary)',
};

const mainGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '20px',
  minHeight: '500px',
};

const listContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
  maxHeight: '600px',
  overflowY: 'auto',
};

const emptyStateStyle: CSSProperties = {
  padding: '40px 20px',
  textAlign: 'center',
};

const emptyTextStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '1rem',
};

const listItemStyle: CSSProperties = {
  padding: '16px',
  borderBottom: '1px solid var(--color-border)',
  cursor: 'pointer',
  transition: 'background-color 0.15s',
};

const selectedItemStyle: CSSProperties = {
  backgroundColor: 'var(--color-badge-info-bg)',
  borderLeftColor: 'var(--color-primary)',
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
};

const listItemHeaderStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginBottom: '8px',
  flexWrap: 'wrap',
};

const badgeStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  borderRadius: '9999px',
  textTransform: 'capitalize',
};

const listItemDescriptionStyle: CSSProperties = {
  fontSize: '0.9375rem',
  color: 'var(--color-text-primary)',
  margin: '0 0 8px 0',
  lineHeight: 1.5,
};

const listItemFooterStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listItemMetaStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-text-muted)',
};

const detailPanelStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '12px',
  border: '1px solid var(--color-border)',
  overflow: 'hidden',
  maxHeight: '600px',
  overflowY: 'auto',
};

const detailHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const detailTitleStyle: CSSProperties = {
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const closeDetailButtonStyle: CSSProperties = {
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
};

const detailContentStyle: CSSProperties = {
  padding: '20px',
};

const detailSectionStyle: CSSProperties = {
  marginBottom: '20px',
};

const detailSectionTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
};

const statusButtonsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const statusButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const activeStatusButtonStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  borderColor: 'var(--color-primary)',
  color: 'white',
};

const statusErrorStyle: CSSProperties = {
  marginTop: '8px',
  padding: '8px 12px',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-error)',
  fontSize: '0.875rem',
  borderRadius: '6px',
  border: '1px solid var(--color-error)',
};

const detailBadgesStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
};

const detailDescriptionStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary)',
  lineHeight: 1.6,
  margin: 0,
  whiteSpace: 'pre-wrap',
};

const screenshotStyle: CSSProperties = {
  maxWidth: '100%',
  maxHeight: '200px',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
};

const detailTextStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-primary)',
  margin: '4px 0',
};

const linkStyle: CSSProperties = {
  color: 'var(--color-primary)',
  wordBreak: 'break-all',
};

const invalidUrlStyle: CSSProperties = {
  color: 'var(--color-error)',
  fontStyle: 'italic',
  wordBreak: 'break-all',
};

const loadMoreContainerStyle: CSSProperties = {
  padding: '16px',
  textAlign: 'center',
  borderTop: '1px solid var(--color-border)',
};

const loadMoreButtonStyle: CSSProperties = {
  padding: '10px 24px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-primary)',
  borderRadius: '6px',
  cursor: 'pointer',
};

const notesSectionHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const editNotesButtonStyle: CSSProperties = {
  padding: '4px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--color-primary)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-primary)',
  borderRadius: '4px',
  cursor: 'pointer',
};

const notesTextareaStyle: CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const notesActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  marginTop: '8px',
  justifyContent: 'flex-end',
};

const cancelNotesButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  cursor: 'pointer',
};

const saveNotesButtonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'white',
  backgroundColor: 'var(--color-primary)',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const emptyNotesStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-muted)',
  fontStyle: 'italic',
};

const footerStatsStyle: CSSProperties = {
  marginTop: '16px',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  textAlign: 'center',
};

export default AdminFeedbackPage;
